import { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { applyElementProps, APARTE_DEFAULT_UI_EVENTS, type AparteUiHandle } from '@aparte/core';

export interface AparteUiProps {
    /** The custom element tag name (e.g. 'aparte-model-selector'). */
    name: string;
    /** Props to apply. Keys starting with `--` become CSS variables. */
    props?: Record<string, unknown>;
    /** Emits a forwarded custom event from the underlying Web Component. */
    onElementEvent?: (event: CustomEvent) => void;
    /**
     * Which custom events to forward through `onElementEvent`. Defaults to the
     * interactive aparté surface ({@link APARTE_DEFAULT_UI_EVENTS}); pass your own list to
     * listen to other events (e.g. `['aparte-composer-change']` for attachments).
     */
    events?: string[];
}

// The handle is core's, not this wrapper's: the same two-method contract was
// hand-copied into all four, each docblock promising "the same on all four", and it
// had already drifted (two of them returned `HTMLElement | null` / `unknown` instead
// of the generic form). One declaration in `@aparte/core`, four re-exports.
export type { AparteUiHandle } from '@aparte/core';

/**
 * Universal pass-through proxy: dynamically mounts any `aparte-*` Web Component so
 * you don't need a dedicated React wrapper per element. React equivalent of
 * Angular's `AparteUiComponent`.
 *
 * `name` can be any custom element, including one of yours. `aparte-model-selector`
 * below is defined by `@aparte/plugin-model-selector`, not by core — until that
 * package is imported the tag mounts empty and inert with no error, and upgrades on
 * its own once the definition arrives (which is what makes lazy-loading it work).
 *
 * A PLUGIN's event is not in the default forward list — that default is core's, and a
 * third-party plugin's author cannot add to it, so listing ours would privilege our
 * packages over theirs. Name it in `events` and it arrives; leave it out and the handler
 * silently never fires, which is what this example used to do.
 *
 * @example
 * <AparteUi
 *   name="aparte-model-selector"
 *   props={{ placeholder: 'Ask…', '--glow-speed': '4s' }}
 *   events={['aparte-model-change']}
 *   onElementEvent={onEvent}
 * />
 */
export const AparteUi = forwardRef<AparteUiHandle, AparteUiProps>(function AparteUi(
    { name, props = {}, onElementEvent, events },
    ref,
) {
    const hostRef = useRef<HTMLSpanElement>(null);
    const elRef = useRef<HTMLElement | null>(null);
    const cbRef = useRef(onElementEvent);
    cbRef.current = onElementEvent;
    const propsRef = useRef(props);
    propsRef.current = props;

    const evts = events ?? APARTE_DEFAULT_UI_EVENTS;
    const evtsKey = evts.join('|');

    // (Re)create the element when `name` (or the forwarded event set) changes.
    useEffect(() => {
        const host = hostRef.current;
        if (!host) return;
        const el = document.createElement(name);
        elRef.current = el;
        // The props effect below compares `props` by identity, so a memoized prop bag
        // never re-applies on a `name`/`events` change. Apply through a ref here — the
        // same shape Vue/Svelte/Angular use inside their own create().
        applyElementProps(el, propsRef.current);
        const cleanups: Array<() => void> = [];
        for (const ev of evtsKey.split('|').filter(Boolean)) {
            const listener = (e: Event) => cbRef.current?.(e as CustomEvent);
            el.addEventListener(ev, listener);
            cleanups.push(() => el.removeEventListener(ev, listener));
        }
        host.appendChild(el);
        return () => {
            for (const c of cleanups) c();
            el.remove();
            elRef.current = null;
        };
    }, [name, evtsKey]);

    // Apply props whenever they change.
    useEffect(() => {
        if (elRef.current) applyElementProps(elRef.current, props);
    }, [props]);

    useImperativeHandle(ref, (): AparteUiHandle => ({
        getElement: () => elRef.current as never,
        callMethod: (methodName, ...args) => {
            const fn = (elRef.current as unknown as Record<string, unknown>)?.[methodName];
            return typeof fn === 'function'
                ? (fn as (...a: unknown[]) => unknown).apply(elRef.current, args) as never
                : undefined;
        },
    }), []);

    return <span ref={hostRef} style={{ display: 'contents' }} />;
});

AparteUi.displayName = 'AparteUi';
